import {
  Globe,
  Network,
  BarChart3,
  Receipt,
  Settings,
  type LucideIcon,
} from 'lucide-react';

/** A nested nav child rendered inside a collapsible parent group. */
export interface CdnNavChild {
  label: string;
  /** Path segment under `/services/cdn/:serviceId` (e.g. 'analytics/monitoring'). */
  slug: string;
}

export interface CdnNavEntry {
  label: string;
  /** Path segment under `/services/cdn/:serviceId` ('' = console root). */
  slug: string;
  icon: LucideIcon;
  /** When present, the entry is a collapsible parent; `slug` is its default child target. */
  children?: CdnNavChild[];
  /**
   * Absolute target that overrides the base-relative `cdnHref(base, slug)`. Used
   * for entries that leave the service console — Business Usage now points at the
   * org-wide `/services/cdn/usage` page (filterable by service) instead of a
   * per-service view.
   */
  href?: string;
}

/**
 * CDN orchestrator console navigation entries. Slugs map 1:1 to the nested
 * routes in CdnConsole. Rendered in the app's scope-driven ContextualSidebar.
 */
export const CDN_NAV: CdnNavEntry[] = [
  { label: 'Overview', slug: '', icon: Globe },
  { label: 'Domains', slug: 'domains', icon: Network },
  {
    label: 'Analytics',
    slug: 'analytics',
    icon: BarChart3,
    children: [
      { label: 'Monitoring', slug: 'analytics/monitoring' },
      { label: 'Service Overview', slug: 'analytics/overview' },
      { label: 'Viewer Location', slug: 'analytics/location' },
    ],
  },
  // Leaves the console for the org-wide usage page (default: all services).
  { label: 'Business Usage', slug: 'usage', icon: Receipt, href: '/services/cdn/usage' },
  { label: 'Settings', slug: 'settings', icon: Settings },
];

/** Absolute href for a slug under the console base ('' slug = base/root). */
export function cdnHref(base: string, slug: string): string {
  return slug ? `${base}/${slug}` : base;
}

/** Every selectable href under the console (parents flattened to their children). */
function allHrefs(base: string): string[] {
  return CDN_NAV.flatMap((e) =>
    e.children ? e.children.map((c) => cdnHref(base, c.slug)) : [cdnHref(base, e.slug)],
  );
}

/**
 * The active entry href for the current pathname (longest-prefix match).
 * Both `base` and `base/overview` resolve to the Overview entry ('' slug);
 * the bare `base/analytics` resolves to its first child (Monitoring).
 */
export function cdnActiveId(base: string, pathname: string): string {
  // Normalize aliases: `/overview` → root; bare `/analytics` → its first child.
  const normalized =
    pathname === `${base}/overview`
      ? base
      : pathname === `${base}/analytics`
        ? `${base}/analytics/monitoring`
        : pathname;
  const matches = allHrefs(base)
    .filter((href) => normalized === href || normalized.startsWith(`${href}/`))
    .sort((a, b) => b.length - a.length);
  return matches[0] ?? base;
}
